import { AlertTriangle, Check, Pause, Play, Square, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { MdAutoMode } from 'react-icons/md';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { computeChaseState } from '@/hooks/useAutoChase';
import { cn } from '@/lib/utils';
import { useUpdateIntakeChaseStatusMutation } from '@/services/api/intakeChaseService';
import {
  type IntakeChase,
  IntakeChaseMessageStatus,
  IntakeChaseStatus,
} from '@/services/types/intake-chase-types';

interface AutoChaseTableCellProps {
  chase: IntakeChase | null;
}

type ConfirmAction = 'pause' | 'resume' | 'stop' | null;

/**
 * Renders the Auto Chase status in the intakes table with inline quick actions.
 * Hover reveals pause/resume/stop controls; clicking an action opens a confirmation dialog.
 */
export const AutoChaseTableCell: React.FC<AutoChaseTableCellProps> = ({
  chase,
}) => {
  const [updateStatus, { isLoading: isUpdating }] =
    useUpdateIntakeChaseStatusMutation();
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);

  const {
    currentDay,
    currentChase,
    totalMessagesSent,
    totalMessages,
    failedMessageCount,
    failureReason,
    canPause,
    canResume,
    canStop,
  } = useMemo(() => computeChaseState(chase), [chase]);

  // Simplify failure reason for display (strip nested exception chains)
  const simplifiedReason = useMemo(() => {
    if (!failureReason) return null;
    // Extract the most useful part: look for HTTP status codes or key phrases
    const match = failureReason.match(/(\d{3}\s+\w[\w\s]*?)(?:\s+for\s+url:|$)/);
    if (match) return match[1].trim();
    // Fallback: truncate to reasonable length
    return failureReason.length > 120 ? `${failureReason.slice(0, 120)}…` : failureReason;
  }, [failureReason]);

  if (!chase) {
    return <span className="text-gray-500 text-xs font-medium">--</span>;
  }

  const { status } = chase;

  const handleConfirmAction = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirmAction) return;

    const statusMap: Record<string, 'paused' | 'active' | 'stopped'> = {
      pause: 'paused',
      resume: 'active',
      stop: 'stopped',
    };

    try {
      await updateStatus({
        intakeId: chase.intake,
        body: { status: statusMap[confirmAction] },
      }).unwrap();

      const labels: Record<string, string> = {
        pause: 'Auto chase paused',
        resume: 'Auto chase resumed',
        stop: 'Auto chase stopped',
      };
      toast.success(labels[confirmAction]);
    } catch {
      toast.error(`Failed to ${confirmAction} auto chase`);
    } finally {
      setConfirmAction(null);
    }
  };

  const getDialogContent = () => {
    const messages = chase.messages || [];
    const remainingMessages = messages.length > 0
      ? messages.filter(
          (m) =>
            m.status === IntakeChaseMessageStatus.PENDING ||
            m.status === IntakeChaseMessageStatus.SENDING,
        ).length
      : (chase.message_count ?? 0) - (chase.sent_count ?? 0);

    switch (confirmAction) {
      case 'pause':
        return {
          title: 'Pause Auto Chase',
          description:
            'Pause Auto Chase? No texts will be sent until you resume.',
          actionLabel: 'Pause',
          variant: 'default' as const,
        };
      case 'resume':
        return {
          title: 'Resume Auto Chase',
          description: `Resume Auto Chase? ${remainingMessages} text${remainingMessages !== 1 ? 's' : ''} remaining.`,
          actionLabel: 'Resume',
          variant: 'default' as const,
        };
      case 'stop':
        return {
          title: 'Stop Auto Chase',
          description: `Stop Auto Chase? This will permanently cancel ${remainingMessages} remaining text${remainingMessages !== 1 ? 's' : ''}. This cannot be undone.`,
          actionLabel: 'Stop Chase',
          variant: 'destructive' as const,
        };
      default:
        return {
          title: '',
          description: '',
          actionLabel: '',
          variant: 'default' as const,
        };
    }
  };

  const dialogContent = getDialogContent();

  // Quick action buttons (shown on hover via group-hover)
  const renderQuickActions = () => {
    if (!canPause && !canResume && !canStop) return null;

    return (
      <div className="hidden group-hover/chase:flex items-center gap-0.5 ml-auto">
        {canPause && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmAction('pause');
                }}
              >
                <Pause className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">Pause chase</p>
            </TooltipContent>
          </Tooltip>
        )}
        {canResume && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmAction('resume');
                }}
              >
                <Play className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">Resume chase</p>
            </TooltipContent>
          </Tooltip>
        )}
        {canStop && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 text-red-500 hover:text-red-700 hover:bg-red-50"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmAction('stop');
                }}
              >
                <Square className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">Stop chase</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    );
  };

  const failedBadge = failedMessageCount > 0 ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center gap-0.5 text-red-600 shrink-0">
          <AlertTriangle className="h-3 w-3" />
          <span className="text-[10px] font-bold">{failedMessageCount}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        <p className="text-xs font-medium">
          {failedMessageCount} message{failedMessageCount !== 1 ? 's' : ''} failed to send
        </p>
        {simplifiedReason && (
          <p className="text-xs text-zinc-400 mt-0.5">{simplifiedReason}</p>
        )}
      </TooltipContent>
    </Tooltip>
  ) : null;

  const renderStatus = () => {
    switch (status) {
      case IntakeChaseStatus.ACTIVE:
        return (
          <>
            <span className="w-2 h-2 rounded-full bg-green-500 shrink-0 animate-pulse" />
            <span className="text-xs font-medium text-zinc-700 whitespace-nowrap">
              {currentDay > 0
                ? `D${currentDay}/C${currentChase} · ${totalMessagesSent}/${totalMessages}`
                : 'Starting...'}
            </span>
            {failedBadge}
          </>
        );

      case IntakeChaseStatus.NEEDS_ATTENTION:
        return (
          <>
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0" />
            <span className="text-xs font-bold text-amber-700 whitespace-nowrap">
              PC Responded
            </span>
          </>
        );

      case IntakeChaseStatus.PAUSED:
        return (
          <>
            <Pause className="h-3 w-3 text-zinc-500 shrink-0" />
            <span className="text-xs font-medium text-zinc-600 whitespace-nowrap">
              Paused{currentDay > 0 ? ` · D${currentDay}/C${currentChase}` : ''}
            </span>
            {failedBadge}
          </>
        );

      case IntakeChaseStatus.COMPLETED:
        return (
          <>
            <Check className="h-3.5 w-3.5 text-green-600 shrink-0" />
            <span className="text-xs font-medium text-zinc-500">Completed</span>
            {failedBadge}
          </>
        );

      case IntakeChaseStatus.STOPPED:
        return (
          <>
            <X className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
            <span className="text-xs font-medium text-zinc-500">Stopped</span>
            {failedBadge}
          </>
        );

      default:
        return (
          <span className="text-gray-500 text-xs font-medium">--</span>
        );
    }
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="group/chase flex items-center gap-1.5 w-full">
        {renderStatus()}
        {renderQuickActions()}
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog
        open={confirmAction !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
      >
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>{dialogContent.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {dialogContent.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={(e) => e.stopPropagation()}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAction}
              disabled={isUpdating}
              className={cn(
                dialogContent.variant === 'destructive' &&
                  'bg-red-600 hover:bg-red-700 text-white',
              )}
            >
              {isUpdating && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5" />
              )}
              {dialogContent.actionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
};

/**
 * Compact inline chase indicator for non-chase-view table rows.
 * Shows a small icon next to the status when an intake has an active chase.
 */
export const AutoChaseIndicator: React.FC<{ chase: IntakeChase | null }> = ({
  chase,
}) => {
  if (!chase) return null;

  const { status } = chase;

  // Only show indicator for active statuses
  if (
    status !== IntakeChaseStatus.ACTIVE &&
    status !== IntakeChaseStatus.NEEDS_ATTENTION &&
    status !== IntakeChaseStatus.PAUSED
  ) {
    return null;
  }

  const config = {
    [IntakeChaseStatus.ACTIVE]: {
      icon: <MdAutoMode className="h-3 w-3" />,
      className: 'text-green-600',
      tooltip: 'Auto Chase active',
    },
    [IntakeChaseStatus.NEEDS_ATTENTION]: {
      icon: <AlertTriangle className="h-3 w-3" />,
      className: 'text-amber-600',
      tooltip: 'PC responded -- needs attention',
    },
    [IntakeChaseStatus.PAUSED]: {
      icon: <Pause className="h-3 w-3" />,
      className: 'text-zinc-500',
      tooltip: 'Auto Chase paused',
    },
  }[status];

  if (!config) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn('inline-flex shrink-0', config.className)}>
            {config.icon}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="text-xs font-medium">{config.tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
